
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Linkedin } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLinkedInConnection } from '@/hooks/useLinkedInConnection';

interface LinkedInConnectProps {
  onSuccess?: () => void;
  size?: 'default' | 'sm' | 'lg' | 'icon';
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
}

// Set the redirect URI to match the authorized LinkedIn redirect URI
const getRedirectUri = () => {
  return window.location.origin + '/auth'; // Always use /auth as redirect URI
};

export const LinkedInConnect = ({ 
  onSuccess, 
  size = 'default',
  variant = 'default'
}: LinkedInConnectProps) => {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { connection, isConnected, disconnect } = useLinkedInConnection();

  const initiateOAuth = async () => {
    console.log('[LinkedIn OAuth] Starting OAuth flow. User authenticated:', !!user);
    
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in before connecting your LinkedIn account",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      console.log('[LinkedIn OAuth] Initiating LinkedIn OAuth flow');
      
      // Create a state parameter containing the user ID
      const state = btoa(JSON.stringify({
        userId: user.id,
        timestamp: Date.now(),
        flow: 'content_posting' // Identify this flow as for content posting, not authentication
      }));

      const redirectUri = getRedirectUri();
      console.log('[LinkedIn OAuth] Using redirect URI:', redirectUri);
      console.log('[LinkedIn OAuth] Current path:', window.location.pathname);

      // LinkedIn OAuth parameters
      const params = new URLSearchParams({
        response_type: 'code',
        client_id: import.meta.env.VITE_LINKEDIN_CLIENT_ID || '',
        redirect_uri: redirectUri,
        state,
        scope: 'r_liteprofile r_emailaddress w_member_social',
      });

      const authUrl = `https://www.linkedin.com/oauth/v2/authorization?${params.toString()}`;
      console.log('[LinkedIn OAuth] Redirecting to LinkedIn authorization URL:', authUrl);
      
      // Redirect the user to LinkedIn's authorization page
      window.location.href = authUrl;
      
    } catch (error) {
      console.error('[LinkedIn OAuth] Error initiating OAuth:', error);
      toast({
        title: "Connection failed",
        description: "Could not connect to LinkedIn. Please try again.",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const disconnectLinkedIn = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      if (disconnect) {
        await disconnect();
        onSuccess?.();
      } else {
        // Fallback if the hook method is not available
        const { error } = await supabase
          .from('linkedin_oauth_tokens')
          .delete()
          .eq('user_id', user.id);
          
        if (error) throw error;
        
        // Invalidate queries that might depend on LinkedIn connection status
        queryClient.invalidateQueries({ queryKey: ['linkedinConnection', user.id] });
        
        toast({
          title: "LinkedIn disconnected",
          description: "Your LinkedIn account has been disconnected successfully.",
        });
      }
      
    } catch (error) {
      console.error('Error disconnecting LinkedIn:', error);
      toast({
        title: "Disconnection failed",
        description: "Could not disconnect your LinkedIn account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (isConnected) {
    return (
      <Button
        variant="outline"
        size={size}
        disabled={loading}
        onClick={disconnectLinkedIn}
        className="text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200"
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : (
          <Linkedin className="h-4 w-4 mr-2" />
        )}
        Disconnect LinkedIn
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      disabled={loading}
      onClick={initiateOAuth}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
      ) : (
        <Linkedin className="h-4 w-4 mr-2" />
      )}
      Connect LinkedIn
    </Button>
  );
};
